/// 日本の郵便番号 (branded 型 + 構文バリデーション)。
///
/// 対象フィールド: `firms.postal_code` / `contracts.pro_billing_postal_code` など。
///
/// バリデーション規則は backend `PostalCode` VO (`domain/src/core/value_objects.rs`) と揃える:
///   - 形式は `NNN-NNNN` 厳密一致 (ASCII 数字のみ、合計 8 文字)
///   - ハイフンなし 7 桁 (`1234567`) → null
///   - 全角数字 (`１００-０００１`) → null
///   - 前後 whitespace → null
///   - それ以外 (長さ不一致・非数字・ハイフン位置違い) → null
///   - 入力文字列をそのまま保持 (正規化なし)

/** 構文検証済みの日本郵便番号 (`NNN-NNNN` 形式)。 */
export type PostalCode = string & { readonly __brand: 'PostalCode' };

/** `NNN-NNNN` 形式の正規表現 (ASCII 数字のみ)。 */
const POSTAL_CODE_RE = /^[0-9]{3}-[0-9]{4}$/;

/**
 * `value` を検証し、問題なければ `PostalCode` 型で返す。
 *
 * 無効な場合は `null` を返す (例外は投げない)。
 *
 * - `NNN-NNNN` 形式 (ASCII 数字 3 桁 + ハイフン + ASCII 数字 4 桁) のみ有効。
 * - ハイフンなし 7 桁・全角数字・前後 whitespace・その他形式はすべて無効。
 */
export function createPostalCode(value: string): PostalCode | null {
  // Backend の実装: バイト列で厳密チェック (len==8, bytes[3]=='-', bytes[0..3] と bytes[4..8] が ASCII digit)
  // JS では正規表現で同等チェック: `^[0-9]{3}-[0-9]{4}$`
  // これにより:
  //   - 全角数字 (マルチバイト) は `[0-9]` にマッチしない → 弾く
  //   - 長さ違い / ハイフン位置違い / 余分な文字 → マッチしない → 弾く
  //   - 前後 whitespace → `^` / `$` で弾く
  if (!POSTAL_CODE_RE.test(value)) {
    return null;
  }

  return value as PostalCode;
}
